import { Dispatch, ReactNode, SetStateAction } from "react"

import { ModifiedIncident } from ".."
import { InputField } from "src/components/common/inputfield"
import { RichTextEditor } from "src/components/common/rich-text-editor"
import { defaultFormats } from "src/components/common/rich-text-editor/defaultConfig"
import { Select, SelectOption } from "src/components/common/select"
import { create } from "src/helpers/bem"

import { useTranslation } from "src/hooks/translation"
import { useCountries } from "src/states/countries"
import { Dialogs, useDialog } from "src/states/dialogs"
import { useOptionsets } from "src/states/optionsets"

import styles from "./AuthorizationRequestFields.module.scss"
import AuthorizationRequestFileUpload from "./AuthorizationRequestFileUpload"

const bem = create(styles, "AuthorizationRequestFields")

export enum ClientLocation {
  CompanyInGermany = 761330000,
  ComanpyInEu = 761330001,
  CompanyOutsideEu = 761330002,
}

type AuthorizationRequestFieldsProps = {
  files: File[]
  incident: ModifiedIncident
  setFiles: Dispatch<SetStateAction<File[]>>
  setIncident: Dispatch<SetStateAction<ModifiedIncident>>
}

export const AuthorizationRequestFields = ({
  files,
  incident,
  setFiles,
  setIncident,
}: AuthorizationRequestFieldsProps) => {
  const countries = useCountries()
  const { languages, ustDeclarations } = useOptionsets()
  const { messages: translations } = useTranslation()
  const { disabled: isReadOnly } = useDialog(
    "incidentDialog",
  ) as Dialogs["incidentDialog"]

  const {
    companyName,
    companyStreet,
    companyZipCode,
    companyCity,
    companyCountryCodeId,
    companyEmail,
    companyPhone,
    language,
    description,
    companyClientLocation,
    clientVatId,
  } = incident
  const messages =
    translations.pages.account.overview.profile.incidents.createDialog
      .authorizationRequestFields

  const countryOptions = countries as SelectOption[]
  const languageOptions = languages as SelectOption[]
  const ustDeclarationOptions = ustDeclarations as SelectOption[]

  const companyClientLocationInfoText: Record<number, ReactNode> = {
    761330000: null,
    761330001: (
      <div className={bem("client__location__info__text")}>
        {
          translations.pages.account.overview.webshop.form.clientLocation.fields
            .clientVatIdDescription
        }
      </div>
    ),
    761330002: (
      <div className={bem("client__location__info__text")}>
        {
          translations.pages.account.overview.webshop.form.clientLocation.fields
            .businessCertificateDescription
        }
      </div>
    ),
  }

  const companyClientLocationAdditionalFields: Record<
    ClientLocation,
    ReactNode
  > = {
    761330000: null,
    761330001: (
      <InputField
        disabled={isReadOnly}
        label={messages.clientVatId}
        required={true}
        sizeVariant="large"
        value={clientVatId}
        helperText={
          translations.pages.account.overview.webshop.form.clientLocation.fields
            .clientVatIdHelperText
        }
        onChange={(e) =>
          setIncident({ ...incident, clientVatId: e.target.value })
        }
      />
    ),
    761330002: (
      <AuthorizationRequestFileUpload files={files} setFiles={setFiles} />
    ),
  }

  return (
    <div className={bem()}>
      <div className={bem("grid")}>
        <div className={bem("grid__item")}>
          <InputField
            disabled={isReadOnly}
            label={messages.companyName}
            required={true}
            value={companyName}
            onChange={(e) =>
              setIncident({ ...incident, companyName: e.target.value })
            }
          />
        </div>
        <div className={bem("grid__item")}>
          <InputField
            disabled={isReadOnly}
            label={messages.companyStreet}
            required={true}
            value={companyStreet}
            onChange={(e) =>
              setIncident({ ...incident, companyStreet: e.target.value })
            }
          />
        </div>
        <div className={bem("grid__item")}>
          <InputField
            disabled={isReadOnly}
            label={messages.companyZipCode}
            required={true}
            value={companyZipCode}
            onChange={(e) =>
              setIncident({ ...incident, companyZipCode: e.target.value })
            }
          />
        </div>
        <div className={bem("grid__item")}>
          <InputField
            disabled={isReadOnly}
            label={messages.companyCity}
            required={true}
            value={companyCity}
            onChange={(e) =>
              setIncident({ ...incident, companyCity: e.target.value })
            }
          />
        </div>
        <div className={bem("grid__item")}>
          <Select
            disabled={isReadOnly}
            items={countryOptions}
            label={messages.companyCountryCodeId}
            required={true}
            sizeVariant="medium"
            value={companyCountryCodeId}
            onChange={(e) =>
              setIncident({
                ...incident,
                companyCountryCodeId: e.target.value,
              })
            }
          />
        </div>
        <div className={bem("grid__item")}>
          <Select
            disabled={isReadOnly}
            items={languageOptions}
            label={messages.language}
            required={true}
            sizeVariant="medium"
            value={language}
            onChange={(e) =>
              setIncident({
                ...incident,
                language: Number(e.target.value),
              })
            }
          />
        </div>
        <div className={bem("grid__item")}>
          <InputField
            disabled={isReadOnly}
            label={messages.companyPhone}
            required={true}
            value={companyPhone}
            onChange={(e) =>
              setIncident({ ...incident, companyPhone: e.target.value })
            }
          />
        </div>
        <div className={bem("grid__item")}>
          <InputField
            disabled={isReadOnly}
            label={messages.companyEmail}
            required={true}
            value={companyEmail}
            onChange={(e) =>
              setIncident({ ...incident, companyEmail: e.target.value })
            }
          />
        </div>

        <div className={bem("grid__item__big")}>
          <div className={bem("description")}>
            <h5 className={bem("description__label")}>
              {messages.description.label}
            </h5>
            <RichTextEditor
              formats={defaultFormats.filter((f) => f !== "image")}
              placeholder={messages.description.placeholder}
              readOnly={isReadOnly}
              value={description ?? ""}
              modules={{
                toolbar: [
                  ["bold", "italic", "underline", "strike"],
                  [
                    { list: "ordered" },
                    { list: "bullet" },
                    { indent: "-1" },
                    { indent: "+1" },
                  ],
                ],
              }}
              onChange={(value) =>
                setIncident((prev) => ({ ...prev, description: value }))
              }
            />
          </div>
        </div>

        <div className={bem("grid__item__big")}>
          <Select
            disabled={isReadOnly}
            items={ustDeclarationOptions}
            label={messages.clientLocation}
            required={true}
            value={companyClientLocation}
            helperText={
              translations.pages.account.overview.webshop.form.clientLocation
                .fields.companyClientLocationHelperText
            }
            onChange={(e) =>
              setIncident({
                ...incident,
                companyClientLocation: Number(e.target.value),
              })
            }
          />
        </div>

        {companyClientLocation && (
          <>
            <div className={bem("grid__item__big")}>
              {
                companyClientLocationInfoText[
                  companyClientLocation as ClientLocation
                ]
              }
            </div>

            <div className={bem("grid__item__big")}>
              {
                companyClientLocationAdditionalFields[
                  companyClientLocation as ClientLocation
                ]
              }
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AuthorizationRequestFields
